import { useFormik } from 'formik'
import { useCallback, useContext, useState } from 'react'
import { useQuery } from 'react-query'
import { useLocation } from 'react-router'
import { toast } from 'react-toastify'
import * as Yup from 'yup'

import { AuthContext } from 'features/auth'
import { useProjectStore } from 'features/project/store'
import { useCanShareWith } from 'features/share/hooks'
import type { ShareService } from 'features/share/service'
import type { ProjectState } from 'types/project'

const validationSchema = Yup.object().shape({
  email: Yup.string().email('Invalid email').required('Field is required'),
})

export function useShareDashboard(
  service: ShareService,
  projectId: string,
  onNext: (email: string) => void,
): {
  sharedUsers: import('features/share/types').SharedUsersResponse | undefined
  isLoading: boolean
  formik: ReturnType<typeof useFormik<{ role: string; email: string }>>
  isAdmin: boolean
  userEmail: string | undefined
  errorMessage: string
  shareUrlExpanded: string
  isSharingUrlLoading: boolean
  isLocalLoading: boolean
  copyToClipboard: () => void
  enableSharing: () => void
  disableSharing: () => void
  removeEmail: (email: string) => Promise<void>
} {
  const location = useLocation()
  const { userDetails } = useContext(AuthContext)
  const details = useProjectStore((state: ProjectState) => state.details)
  const isAdmin = details.role === 'admin'
  const canShareWith = useCanShareWith(service, projectId)

  const [isLocalLoading, setLocalLoading] = useState(false)
  const [isSharingEnabled, setSharingEnabled] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  const {
    data: sharedUsers,
    isLoading,
    refetch,
  } = useQuery(['shared-users', projectId], () =>
    service.fetchSharedUsers(projectId),
  )

  const {
    data: shareUrl,
    isLoading: isSharingUrlLoading,
    refetch: fetchUrl,
  } = useQuery(
    `token-${projectId}`,
    () => service.fetchUrlForSharing(projectId, isSharingEnabled),
    { enabled: false },
  )

  const formik = useFormik({
    validationSchema,
    initialValues: { role: 'viewer', email: '' },
    onSubmit: async ({ email, role }, { setFieldError, resetForm }) => {
      setErrorMessage('')
      const normalizedEmail = email.toLowerCase()

      if (!canShareWith(normalizedEmail)) {
        setFieldError('email', 'You are not allowed to invite this user')
        return
      }

      try {
        const success = await service.shareProjectWithUncheckedUser(
          projectId,
          normalizedEmail,
          role === 'admin',
        )

        if (success) {
          resetForm()
          refetch()
          toast.success('Invite sent successfully')
        } else {
          onNext(email)
        }
      } catch (e: unknown) {
        const axiosError = e as {
          response?: { data?: { detail?: { msg?: string } } }
        }
        setErrorMessage('Error: ' + axiosError?.response?.data?.detail?.msg)
      }
    },
  })

  const shareUrlExpanded = shareUrl ? shareUrl + location.search : ''

  const copyToClipboard = useCallback(() => {
    if (shareUrlExpanded) {
      navigator.clipboard.writeText(shareUrlExpanded).then(() => {
        toast.info('Share URL is copied')
      })
    }
  }, [shareUrlExpanded])

  const enableSharing = useCallback(() => {
    setSharingEnabled(true)
    setTimeout(() => {
      setLocalLoading(true)
      fetchUrl().then(() => setLocalLoading(false))
    }, 0)
  }, [fetchUrl])

  const disableSharing = useCallback(() => {
    setSharingEnabled(false)
    setTimeout(() => {
      setLocalLoading(true)
      fetchUrl().then(() => setLocalLoading(false))
    }, 0)
  }, [fetchUrl])

  const removeEmail = useCallback(
    (email: string) =>
      service.unshareProjectWithUser(projectId, email).then(() => {
        refetch()
      }),
    [service, projectId, refetch],
  )

  return {
    sharedUsers,
    isLoading,
    formik,
    isAdmin,
    userEmail: userDetails?.email,
    errorMessage,
    shareUrlExpanded,
    isSharingUrlLoading,
    isLocalLoading,
    copyToClipboard,
    enableSharing,
    disableSharing,
    removeEmail,
  }
}
